import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {useStylesECDS} from './ecdsStyle';
import config from '../../../../app/config';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {cleanObject} from '../../../../util/patientContextUtil';

const AgreementServiceDetailsV3 = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    const {isSmall} = props;

    // Agreement Service Details Dataset
    const [agreementDetails, setAgreementDetails] = useState({
        CommissioningSerialNumber: '',
        NhsServiceAgreementLineNumber: '',
        ProviderReferenceNumber: '',
        CommissionerReferenceNumber: '',
        OrganisationIdentifier_CodeOfProvider: '',
        OrganisationIdentifier_CodeOfCommissioner: '',
    });

    // For ReadOnly Data
    const disableData = useRef({});

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        serialNumber: false,
        lineNumber: false,
        ProviderRefNumber: false,
        commissionerRefNumber: false,
        orgProviderId: validationEnabled,
        orgCommissionerId: validationEnabled,
    });

    // Initial Data
    useEffect(() => {
        let data = {};
        let dataToBlock = {};

        if (props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.ServiceAgreementDetails) {
            data = {
                ...props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.ServiceAgreementDetails,
            };
        }

        setAgreementDetails((prevState) => ({...prevState, ...data}));
        disableData.current = {...cleanObject(dataToBlock)};
    }, []);

    const updateData = () => {
        const data = {
            CommissioningSerialNumber: agreementDetails.CommissioningSerialNumber,
            NhsServiceAgreementLineNumber: agreementDetails.NhsServiceAgreementLineNumber,
            ProviderReferenceNumber: agreementDetails.ProviderReferenceNumber,
            CommissionerReferenceNumber: agreementDetails.CommissionerReferenceNumber,
            OrganisationIdentifier_CodeOfProvider: agreementDetails.OrganisationIdentifier_CodeOfProvider,
            OrganisationIdentifier_CodeOfCommissioner: agreementDetails.OrganisationIdentifier_CodeOfCommissioner,
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (validate()) {
                updateData();
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [agreementDetails]);

    const changeHandler = (event) => {
        setAgreementDetails((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const validate = () => {
        let check = true;

        if (validationEnabled) {
            const errorData = {
                serialNumber: agreementDetails.CommissioningSerialNumber !== '' ? !props.checkLenght(1, 6, agreementDetails.CommissioningSerialNumber) : false,
                lineNumber: agreementDetails.NhsServiceAgreementLineNumber !== '' ? !props.checkLenght(1, 10, agreementDetails.NhsServiceAgreementLineNumber) : false,
                providerRefNumber: agreementDetails.ProviderReferenceNumber !== '' ? !props.checkLenght(1, 17, agreementDetails.ProviderReferenceNumber) : false,
                commissionerRefNumber: agreementDetails.CommissionerReferenceNumber !== '' ? !props.checkLenght(1, 17, agreementDetails.CommissionerReferenceNumber) : false,
                orgProviderId: !props.checkLenght(3, 5, agreementDetails.OrganisationIdentifier_CodeOfProvider),
                orgCommissionerId: !props.checkLenght(3, 5, agreementDetails.OrganisationIdentifier_CodeOfCommissioner),
            };
            setError(prevState => ({...prevState, ...errorData}));

            for (let prop in errorData) {
                if (errorData[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Service Agreement Details',
                    groupStatus: 'M',
                    mandatory: !agreementDetails.OrganisationIdentifier_CodeOfCommissioner || !agreementDetails.OrganisationIdentifier_CodeOfProvider,
                    errors: !check,
                    pickers: false,
                };
                props.valid(prevState => ({...prevState, 'serviceDetails': validObj}));
            }
        }
        return check;
    };

    return (
        props.accordion === 'Service Agreement Details' &&
        <div className={classes.root}>
            <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="ProviderReferenceNumber"
                        label={t('Provider Reference Number')}
                        onChange={changeHandler}
                        value={agreementDetails.ProviderReferenceNumber || ''}
                        error={error.providerRefNumber}
                        helperText={error.providerRefNumber ? 'Input must be max 17 characters long.' : ''}
                        maxLength={17}
                        readOnly={!!disableData?.current.ProviderReferenceNumber}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="NhsServiceAgreementLineNumber"
                        label={t('NHS Service Agreement Line Number')}
                        onChange={changeHandler}
                        value={agreementDetails.NhsServiceAgreementLineNumber || ''}
                        error={error.lineNumber}
                        helperText={error.lineNumber ? 'Input must be max 10 characters long.' : ''}
                        maxLength={10}
                        readOnly={!!disableData?.current.NhsServiceAgreementLineNumber}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="CommissionerReferenceNumber"
                        label={t('Commissioner Reference Number')}
                        onChange={changeHandler}
                        value={agreementDetails.CommissionerReferenceNumber || ''}
                        error={error.commissionerRefNumber}
                        helperText={error.commissionerRefNumber ? 'Input must be max 17 characters long.' : ''}
                        maxLength={17}
                        readOnly={!!disableData?.current.CommissionerReferenceNumber}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="CommissioningSerialNumber"
                        label={t('Commissioning Serial Number')}
                        onChange={changeHandler}
                        value={agreementDetails.CommissioningSerialNumber || ''}
                        error={error.serialNumber}
                        helperText={error.serialNumber ? 'Input must be max 6 characters long.' : ''}
                        maxLength={6}
                        readOnly={!!disableData?.current.CommissioningSerialNumber}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="OrganisationIdentifier_CodeOfProvider"
                        label={t('Org Id - Code of Provider')}
                        onChange={changeHandler}
                        value={agreementDetails.OrganisationIdentifier_CodeOfProvider || ''}
                        error={error.orgProviderId}
                        helperText={error.orgProviderId ? 'Input must be between 3 and 5 characters long.' : ''}
                        maxLength={5}
                        minLength={3}
                        readOnly={!!disableData?.current.OrganisationIdentifier_CodeOfProvider}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="OrganisationIdentifier_CodeOfCommissioner"
                        label={t('Org Id - Code of Commissioner')}
                        onChange={changeHandler}
                        value={agreementDetails.OrganisationIdentifier_CodeOfCommissioner || ''}
                        error={error.orgCommissionerId}
                        helperText={error.orgCommissionerId ? 'Input must be between 3 and 5 characters long.' : ''}
                        maxLength={5}
                        minLength={3}
                        readOnly={!!disableData?.current.OrganisationIdentifier_CodeOfCommissioner}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default AgreementServiceDetailsV3;
